import { computed, inject } from '@angular/core';
import { map, type Observable } from 'rxjs';
import { signalStore, withComputed, withMethods } from '@ngrx/signals';
import { withEntities } from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { LegalService } from './generated/legal/legal.service';
import type {
  CurrentLegalDocumentsResponseV1,
  LegalDocumentCreateRequestV1Request,
  LegalDocumentV1,
  LegalV1DocumentsListParams,
} from './generated/types';

/**
 * TARG-4 pure: injects `LegalService` directly. Cursor list of every published version
 * (admin «Documentos legales») plus publishing the next one.
 *
 * `publish` does not reload the list: `dt-legal-documents` reloads when the modal closes,
 * like `QuotesStore.createQuote`.
 */
export const LegalDocumentsStore = signalStore(
  withEntities<LegalDocumentV1>(),
  withCursorPagination<LegalDocumentV1, LegalV1DocumentsListParams>(() => {
    const api = inject(LegalService);
    return (query, cursor) =>
      api.legalV1DocumentsList({ ...query, cursor: cursor ?? undefined });
  }),
  withComputed((store) => ({
    /**
     * The current version of each document. The backend sends no flag: the current one is
     * the highest version, and the list comes newest first, so it is the first row of each
     * kind — on whichever page it arrives.
     */
    currentIds: computed<ReadonlySet<number>>(() => {
      const seen = new Set<string>();
      const ids = new Set<number>();
      for (const doc of store.entities()) {
        if (seen.has(doc.kind)) continue;
        seen.add(doc.kind);
        ids.add(doc.id);
      }
      return ids;
    }),
  })),
  withMethods((_store, api = inject(LegalService)) => ({
    /** The version a client accepts now of each document; `null` while never published. */
    currentDocuments(): Observable<CurrentLegalDocumentsResponseV1> {
      return api.legalV1DocumentsCurrentRetrieve();
    },
    /** Publishes the next version: current from now on, every client has to accept it. */
    publish(body: LegalDocumentCreateRequestV1Request): Observable<LegalDocumentV1> {
      return api.legalV1DocumentsCreate(body).pipe(map((res) => res.legal_document));
    },
  })),
);
